import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { parseError } from 'hooks/shared/error';
import apiConfig from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';
import { NotificationType } from './useNotification';
import { ItemType } from 'constants/itemType';

export type UserNotificationDto = {
  userGuid: string;
  notificationId: string;
  groupId: string;
  archived: boolean;
  acknowledged: boolean;
  canArchive: boolean;
  createdAt: string;
  acknowledgedAt: string | null;
  archivedAt: string | null;
};

export enum NotificationActionType {
  ACTIVATION_REQUEST = 'activation_request',
}

export type UserNotificationWithDetails = UserNotificationDto & {
  // Notification fields
  priority: string;
  status: string;
  acknowledgementRequired: boolean;
  sourceItemId: string;
  sourceItemType: ItemType;
  sentAt: string | null;
  scheduledDate: string | null;
  incidentId: string | null;
  aors: string[];

  // Metadata fields
  appMessage: string | null;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  sendEmail: boolean | null;
  sendSms: boolean | null;
  sendTeams: boolean | null;
  actionType: NotificationActionType | null;

  // Creator info
  createdByName: string | null;
  createdAt: string;
};

export const useUserNotifications = () => {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<UserNotificationWithDetails[]>({
    queryKey: ['userNotifications', groupGuid],
    queryFn: async () => {
      const response = await axios.get(apiConfig.apiGateway.userNotifications, {
        params: {
          groupId: groupGuid,
          notificationTypes: [NotificationType.IN_APP],
        },
      });
      return response.data;
    },
    enabled: !!groupGuid,
  });
};

export const useAcknowledgeNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      return await axios.post(
        `${apiConfig.apiGateway.userNotifications}/${data.notificationId}/acknowledge`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useArchiveNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      return await axios.post(
        `${apiConfig.apiGateway.userNotifications}/${data.notificationId}/archive`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
